import { useEffect, useRef, useState } from "react";
import Layout from "@/components/feature/Layout";

export default function AboutPage() {
  const formRef = useRef<HTMLFormElement>(null);
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [formUrl, setFormUrl] = useState("");
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    document.title = "关于我们 | 爱看影院";
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    setFormUrl("https://readdy.ai/api/form/d9nj0tu8mbnljin5a1jg");
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formUrl) return;

    const form = formRef.current;
    if (!form) return;

    const honeypot = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypot && honeypot.value.trim() !== "") {
      setFormStatus("success");
      form.reset();
      setCharCount(0);
      return;
    }

    const formData = new FormData(form);

    try {
      const resp = await fetch(formUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await resp.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (resp.ok && parsed?.code === "OK") {
        setFormStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "提交失败，请稍后重试";
        if (typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam")) {
          setFormError("提交失败，请稍后重试");
        } else {
          setFormError(serverMsg);
        }
        setFormStatus("error");
      }
    } catch {
      setFormError("网络错误，请稍后重试");
      setFormStatus("error");
    }
  };

  return (
    <Layout>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "首页",
                item: "https://aikan.com/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "关于我们",
              },
            ],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: "关于爱看影院",
            description:
              "爱看影院是领先的免费影视大全平台，提供免费电影、免费电视剧、热播综艺在线观看服务。",
            url: "https://aikan.com/about",
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "爱看影院是否需要注册会员？",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "爱看影院完全免费，无需注册即可在线观看所有影视内容。",
                },
              },
              {
                "@type": "Question",
                name: "爱看影院的影片画质如何？",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "爱看影院提供超清、高清等多种画质选择，支持流畅播放不缓冲。",
                },
              },
              {
                "@type": "Question",
                name: "爱看影院支持哪些设备观看？",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "爱看影院支持手机、平板、电脑等全平台在线观看，兼容主流浏览器。",
                },
              },
              {
                "@type": "Question",
                name: "爱看影院是否支持下载？",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "爱看影院为在线观看平台，暂不支持下载功能，但您随时可以免费在线观看所有内容。",
                },
              },
            ],
          }),
        }}
      />

      <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-900/40">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl md:text-5xl font-bold font-heading text-foreground-50 mb-4">
            关于爱看影院
          </h1>
          <p className="text-base md:text-lg text-foreground-400 max-w-2xl mx-auto leading-relaxed">
            爱看影院致力于打造国内领先的免费影视大全平台，为千万用户提供海量高清影视资源。
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-10 md:py-14">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="p-6 rounded-xl bg-background-900/60 border border-background-800/50 text-center">
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-primary-500/10 text-primary-400">
                <i className="ri-movie-line text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-foreground-100 mb-2">海量资源</h3>
              <p className="text-sm text-foreground-400">
                覆盖电影、电视剧、综艺、动漫等全品类影视内容
              </p>
            </div>
            <div className="p-6 rounded-xl bg-background-900/60 border border-background-800/50 text-center">
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-accent-500/10 text-accent-400">
                <i className="ri-speed-up-line text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-foreground-100 mb-2">流畅播放</h3>
              <p className="text-sm text-foreground-400">
                高清画质不缓冲，极速加载流畅观看体验
              </p>
            </div>
            <div className="p-6 rounded-xl bg-background-900/60 border border-background-800/50 text-center">
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-secondary-500/10 text-secondary-400">
                <i className="ri-shield-check-line text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-foreground-100 mb-2">完全免费</h3>
              <p className="text-sm text-foreground-400">
                无需注册无需付费，所有影视内容免费在线观看
              </p>
            </div>
          </div>

          <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-50 mb-4">
            品牌故事
          </h2>
          <div className="text-foreground-300 leading-relaxed space-y-4 mb-12">
            <p>
              爱看影院成立于2024年，是国内领先的<strong>免费影视大全在线观看平台</strong>。我们汇聚了海量的<strong>免费电影</strong>、<strong>免费电视剧</strong>、热播综艺和热门动漫资源，致力于为广大影视爱好者提供一站式的在线观影体验。
            </p>
            <p>
              作为一个专业的<strong>影视大全</strong>平台，爱看影院始终坚持"海量资源、高清画质、流畅播放"的服务理念。无论是最新上映的院线大片，还是经典怀旧的老电影，亦或是热门追更的电视剧，用户都能在爱看影院找到心仪的影视内容。
            </p>
            <p>
              我们的技术团队不断优化播放体验，确保<strong>高清电影在线观看不缓冲</strong>，让用户在追剧观影时享受流畅丝滑的播放体验。平台支持手机、平板、电脑等多种设备，随时随地想看就看。
            </p>
          </div>

          <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-50 mb-6">
            用户口碑
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 mb-12">
            {[
              {
                name: "影迷小陈",
                text: "爱看影院真的太方便了！资源超全，画质清晰，追剧首选平台，推荐给所有朋友了！",
                rating: 5,
              },
              {
                name: "追剧达人",
                text: "用了爱看影院之后就再也没换过平台，免费在线观看不卡顿，真的是追剧神器。",
                rating: 5,
              },
              {
                name: "电影爱好者",
                text: "片库非常丰富，国产片好莱坞大片动画一应俱全，更新速度也快，五星好评！",
                rating: 5,
              },
              {
                name: "上班族小王",
                text: "通勤路上用手机就能免费看高清电影，资源多到看不完，每天必打卡的平台。",
                rating: 5,
              },
            ].map((review, idx) => (
              <div
                key={idx}
                className="p-5 rounded-xl bg-background-900/60 border border-background-800/50"
              >
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(review.rating)].map((_, i) => (
                    <div key={i} className="w-4 h-4 flex items-center justify-center text-accent-400">
                      <i className="ri-star-fill text-sm"></i>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-foreground-300 mb-3 leading-relaxed">
                  {review.text}
                </p>
                <p className="text-xs text-foreground-500 font-medium">{review.name}</p>
              </div>
            ))}
          </div>

          <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-50 mb-6">
            联系我们
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-400 flex-shrink-0">
                  <i className="ri-mail-line"></i>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-foreground-200 mb-1">电子邮箱</h4>
                  <p className="text-sm text-foreground-400">contact@aikanyingyuan.com</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-500/10 text-accent-400 flex-shrink-0">
                  <i className="ri-phone-line"></i>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-foreground-200 mb-1">客服热线</h4>
                  <p className="text-sm text-foreground-400">400-888-6666</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-500/10 text-secondary-400 flex-shrink-0">
                  <i className="ri-map-pin-line"></i>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-foreground-200 mb-1">公司地址</h4>
                  <p className="text-sm text-foreground-400">北京市朝阳区望京科技园A座1208室</p>
                </div>
              </div>
            </div>

            <div>
              <form
                ref={formRef}
                id="contact-form"
                data-readdy-form=""
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                <div>
                  <input
                    type="text"
                    name="name"
                    placeholder="您的姓名"
                    required
                    className="w-full px-4 py-3 rounded-lg bg-background-800/60 border border-background-700/50 text-sm text-foreground-100 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500/50 transition-colors"
                  />
                </div>
                <div>
                  <input
                    type="email"
                    name="email"
                    placeholder="您的邮箱"
                    required
                    className="w-full px-4 py-3 rounded-lg bg-background-800/60 border border-background-700/50 text-sm text-foreground-100 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500/50 transition-colors"
                  />
                </div>
                <div>
                  <textarea
                    name="message"
                    placeholder="请输入您的留言（500字以内）"
                    rows={4}
                    maxLength={500}
                    required
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full px-4 py-3 rounded-lg bg-background-800/60 border border-background-700/50 text-sm text-foreground-100 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500/50 transition-colors resize-none"
                  ></textarea>
                  <p className="text-xs text-foreground-500 mt-1 text-right">{charCount}/500</p>
                </div>
                <div className="honeypot-field" aria-hidden="true">
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="w-full px-4 py-3 rounded-lg bg-background-800/60 border border-background-700/50 text-sm"
                  />
                </div>
                <button
                  type="submit"
                  className="w-full py-3 rounded-lg bg-primary-500 text-background-950 text-sm font-semibold hover:bg-primary-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  发送留言
                </button>
                {formStatus === "success" && (
                  <p className="text-sm text-accent-400 text-center">留言发送成功！我们会尽快回复您。</p>
                )}
                {formStatus === "error" && (
                  <p className="text-sm text-red-400 text-center">{formError}</p>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .honeypot-field {
          position: absolute;
          left: -9999px;
          opacity: 0;
          height: 0;
          width: 0;
          overflow: hidden;
        }
      `}</style>
    </Layout>
  );
}